import { mat4, quat, vec3 } from 'gl-matrix';
import { RenderObject } from '../object/RenderObject';
import { GltfAnimation } from '../animation';

const SPRING_CONFIG = {
  stiffness: 80, // Spring constant — higher = faster oscillation
  damping: 8, // Damping coefficient — higher = less bounce
  propagationDelay: 0.04, // Seconds per hop for wave propagation
  magnitudeFalloff: 0.6, // Per-hop magnitude reduction factor
  maxHops: 7, // Max BFS depth
  maxSprings: 24, // Override budget (32 total - 8 for IK)
  maxDuration: 2.0, // Hard expiry (seconds)
  settleThreshold: 0.001, // Radians — below this, consider settled
  // Structural bone stiffness: bones near skeleton root are stiffer.
  // rootStiffnessMax applies to depth 0 (root), decays toward 1.0 by rootStiffnessDecay.
  rootStiffnessMax: 8.0, // Root bone stiffness multiplier (barely moves)
  rootStiffnessDecay: 3, // Depth at which stiffness multiplier reaches ~1.0
};

interface BoneSpring {
  jointIndex: number;
  angle: number; // current angular displacement (radians)
  velocity: number; // angular velocity (rad/s)
  axis: vec3; // rotation axis (world space, normalized)
  stiffnessScale: number; // per-bone stiffness multiplier (structural bones > 1)
  delay: number; // wave propagation delay (seconds)
  magnitude: number; // initial kick strength (falls off with distance)
  activated: boolean; // has the delay elapsed?
}

interface SpringImpulse {
  renderObject: RenderObject;
  springs: BoneSpring[];
  elapsed: number;
}

export class BoneImpulseSystem {
  private impulses: SpringImpulse[] = [];

  /**
   * Apply a spring-based impulse that propagates outward from the hit bone
   * as a damped wave through the skeleton.
   */
  applyImpulse(
    renderObject: RenderObject,
    jointIndex: number,
    impactDirection: vec3,
    magnitude: number = 0.3,
  ): void {
    const anim = renderObject.animation as GltfAnimation;
    const bindPositions = anim.computeBindPosePositions();
    const parentMap = anim.jointParentMap;

    // Compute root depth for each bone (distance from skeleton root)
    const rootDepth = this.computeRootDepths(parentMap);

    // BFS outward from hit bone — both children and parent directions
    const springs: BoneSpring[] = [];
    const visited = new Set<number>();
    const queue: Array<{ jointIndex: number; hops: number }> = [{ jointIndex, hops: 0 }];
    visited.add(jointIndex);

    while (queue.length > 0) {
      const { jointIndex: idx, hops } = queue.shift()!;
      if (hops > SPRING_CONFIG.maxHops) continue;
      if (springs.length >= SPRING_CONFIG.maxSprings) break;

      const axis = this.computeRotationAxis(idx, parentMap, bindPositions, impactDirection);

      const hopMagnitude = 1.0 / (1.0 + hops * SPRING_CONFIG.magnitudeFalloff);

      // Structural bones near root get higher stiffness (move less)
      const depth = rootDepth[idx];
      const depthRatio = Math.min(depth / SPRING_CONFIG.rootStiffnessDecay, 1.0);
      const stiffnessScale = 1.0 + (SPRING_CONFIG.rootStiffnessMax - 1.0) * (1.0 - depthRatio);

      springs.push({
        jointIndex: idx,
        angle: 0,
        velocity: 0,
        axis,
        stiffnessScale,
        delay: hops * SPRING_CONFIG.propagationDelay,
        magnitude: (hopMagnitude * magnitude) / stiffnessScale,
        activated: false,
      });

      // Enqueue children
      for (let i = 0; i < parentMap.length; i++) {
        if (parentMap[i] === idx && !visited.has(i)) {
          visited.add(i);
          queue.push({ jointIndex: i, hops: hops + 1 });
        }
      }

      // Enqueue parent (wave travels up the chain too)
      const parent = parentMap[idx];
      if (parent >= 0 && !visited.has(parent)) {
        visited.add(parent);
        queue.push({ jointIndex: parent, hops: hops + 1 });
      }
    }

    // Remove any existing impulse on the same render object to avoid conflicts
    for (let i = this.impulses.length - 1; i >= 0; i--) {
      if (this.impulses[i].renderObject === renderObject) {
        this.clearImpulse(this.impulses[i]);
        this.impulses.splice(i, 1);
      }
    }

    this.impulses.push({
      renderObject,
      springs,
      elapsed: 0,
    });
  }

  /**
   * Tick all active spring impulses. Runs damped harmonic oscillator per bone
   * and writes bone overrides.
   */
  tick(dt: number): void {
    for (let i = this.impulses.length - 1; i >= 0; i--) {
      const imp = this.impulses[i];
      imp.elapsed += dt;

      // Hard expiry
      if (imp.elapsed >= SPRING_CONFIG.maxDuration) {
        this.clearImpulse(imp);
        this.impulses.splice(i, 1);
        continue;
      }

      if (!imp.renderObject.animationState) continue;
      const frameSet = imp.renderObject.animationState.getFrames();
      if (!imp.renderObject.boneOverrides) {
        imp.renderObject.boneOverrides = new Map();
      }

      const anim = imp.renderObject.animation as GltfAnimation;
      let allSettled = true;
      let allActivated = true;

      for (const spring of imp.springs) {
        if (imp.elapsed < spring.delay) {
          allActivated = false;
          continue;
        }

        // Kick on first activation
        if (!spring.activated) {
          spring.velocity = spring.magnitude * SPRING_CONFIG.stiffness * 0.5;
          spring.activated = true;
        }

        // Damped harmonic oscillator (structural bones have higher stiffness)
        const k = SPRING_CONFIG.stiffness * spring.stiffnessScale;
        const d = SPRING_CONFIG.damping * Math.sqrt(spring.stiffnessScale);
        const force = -k * spring.angle - d * spring.velocity;
        spring.velocity += force * dt;
        spring.angle += spring.velocity * dt;

        if (
          Math.abs(spring.angle) > SPRING_CONFIG.settleThreshold ||
          Math.abs(spring.velocity) > SPRING_CONFIG.settleThreshold
        ) {
          allSettled = false;
        }

        // Build delta rotation matrix from spring angle + axis
        const deltaQuat = quat.setAxisAngle(quat.create(), spring.axis, spring.angle);
        const deltaM = mat4.fromQuat(mat4.create(), deltaQuat);

        // Get current animated joint matrix
        const joint = anim.joints[spring.jointIndex];
        const frame = frameSet.frames[joint.name];
        if (!frame) continue;

        // Apply: deltaM * originalMatrix (preserves scale)
        const origPos = vec3.fromValues(
          frame.jointMatrix[12],
          frame.jointMatrix[13],
          frame.jointMatrix[14],
        );
        const overrideMatrix = mat4.multiply(mat4.create(), deltaM, frame.jointMatrix);
        overrideMatrix[12] = origPos[0];
        overrideMatrix[13] = origPos[1];
        overrideMatrix[14] = origPos[2];

        imp.renderObject.boneOverrides.set(spring.jointIndex, overrideMatrix);
        this.lockBone(imp.renderObject, spring.jointIndex);
      }

      // Expire when all springs have activated and settled
      if (allActivated && allSettled) {
        this.clearImpulse(imp);
        this.impulses.splice(i, 1);
      }
    }
  }

  /** Compute depth from skeleton root for each joint (root = 0). */
  private computeRootDepths(parentMap: number[]): number[] {
    const depths = new Array<number>(parentMap.length).fill(-1);
    const compute = (idx: number): number => {
      if (depths[idx] >= 0) return depths[idx];
      const parent = parentMap[idx];
      depths[idx] = parent < 0 ? 0 : compute(parent) + 1;
      return depths[idx];
    };
    for (let i = 0; i < parentMap.length; i++) compute(i);
    return depths;
  }

  /**
   * Compute a rotation axis for a bone that makes it bend naturally.
   * Uses cross(boneDirection, impactDirection) so the bone rotates
   * perpendicular to both its own direction and the impact.
   */
  private computeRotationAxis(
    jointIndex: number,
    parentMap: number[],
    bindPositions: vec3[],
    impactDir: vec3,
  ): vec3 {
    const parentIdx = parentMap[jointIndex];
    if (parentIdx < 0) {
      // Root bone: use impact cross up
      const axis = vec3.cross(vec3.create(), impactDir, vec3.fromValues(0, 1, 0));
      const len = vec3.length(axis);
      if (len < 0.001) {
        return vec3.fromValues(1, 0, 0);
      }
      return vec3.scale(axis, axis, 1 / len);
    }

    // Bone direction: parent → child
    const boneDir = vec3.subtract(
      vec3.create(),
      bindPositions[jointIndex],
      bindPositions[parentIdx],
    );
    vec3.normalize(boneDir, boneDir);

    // Rotation axis = cross(boneDir, impactDir)
    const axis = vec3.cross(vec3.create(), boneDir, impactDir);
    const len = vec3.length(axis);
    if (len < 0.001) {
      // Impact along bone — fall back to cross with up
      vec3.cross(axis, impactDir, vec3.fromValues(0, 1, 0));
      const len2 = vec3.length(axis);
      if (len2 < 0.001) return vec3.fromValues(1, 0, 0);
      return vec3.scale(axis, axis, 1 / len2);
    }
    return vec3.scale(axis, axis, 1 / len);
  }

  /** Clean up all overrides and locks for an impulse. */
  private clearImpulse(imp: SpringImpulse): void {
    const overrides = imp.renderObject.boneOverrides;
    for (const spring of imp.springs) {
      if (overrides) {
        overrides.delete(spring.jointIndex);
      }
      imp.renderObject.impulseLockedBones?.delete(spring.jointIndex);
    }
    if (imp.renderObject.impulseLockedBones && imp.renderObject.impulseLockedBones.size === 0) {
      imp.renderObject.impulseLockedBones = undefined;
    }
  }

  private lockBone(renderObject: RenderObject, jointIndex: number): void {
    if (!renderObject.impulseLockedBones) {
      renderObject.impulseLockedBones = new Set();
    }
    renderObject.impulseLockedBones.add(jointIndex);
  }
}
